import { useForm } from "react-hook-form";
import { useResidentContext } from "../provider/ResidentProvider";
import {
  useDeleteHealthConditionMediaMutation,
  useUpdateResidentHealthConditionsMutation,
} from "app/services/api/residentsApi";
import { useState } from "react";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { AddHealthConditionsForm } from "app/services/api/types/residents/models/AddHealthConditionsForm";
import { convertToFile } from "ui/utils/fileUtils";
import { handleBackendError } from "ui/utils/Errors";
import { usePageFocus } from "ui/utils/FocusHook";
import { isDocumentPickerAsset, isMediaModel } from "ui/types/ThrelsFileType";

export const useHandleMedicalHistory = () => {
  const { residentId, resident } = useResidentContext();
  const [triggerUpdateResidentHealthConditions] =
    useUpdateResidentHealthConditionsMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { showNewToast, showErrorToast } = useDisplayToast();
  const [triggerMediaDelete] = useDeleteHealthConditionMediaMutation();
  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { isSubmitting },
  } = useForm<AddHealthConditionsForm>({
    defaultValues: {
      health_conditions: [{ description: "", health_condition_media: [] }],
    },
  });

  usePageFocus(() => {
    const conditions = resident?.health_conditions;
    if (conditions?.length) {
      reset({
        health_conditions: conditions.map(
          ({
            description,
            health_condition_media: healthConditionMedia,
            id,
          }) => ({
            id,
            description: description || "",
            health_condition_media: healthConditionMedia
              ? [healthConditionMedia]
              : [],
          }),
        ),
      });
    }
  }, [resident, reset]);

  const submitForm = () => {
    return new Promise<boolean>((resolve) => {
      if (isSubmitting) {
        resolve(false);
        return;
      }
      handleSubmit(
        async ({ health_conditions: healthConditions }) => {
          try {
            if (!residentId) {
              resolve(false);
              return;
            }

            const deletedMedia: {
              healthConditionId: number;

              mediaId: number;
            }[] = [];

            resident?.health_conditions?.forEach((condition) => {
              const media = condition.health_condition_media;
              if (
                media &&
                !healthConditions.find(
                  (hc) =>
                    hc.id === condition.id &&
                    hc.health_condition_media?.some(
                      (m) => isMediaModel(m) && m.id === media.id,
                    ),
                )
              ) {
                deletedMedia.push({
                  healthConditionId: condition.id,
                  mediaId: media.id,
                });
              }
            });

            if (deletedMedia.length) {
              await Promise.all(
                deletedMedia.map(({ mediaId, healthConditionId }) =>
                  triggerMediaDelete({
                    healthConditionId,
                    mediaId,
                  }).unwrap(),
                ),
              );
            }

            const formData = new FormData();

            healthConditions.forEach((condition, index) => {
              if (condition.id) {
                formData.append(
                  `health_conditions[${index}][id]`,
                  `${condition.id}`,
                );
              }

              if (condition.description) {
                formData.append(
                  `health_conditions[${index}][description]`,
                  condition.description,
                );
              }

              const newFiles: File[] = convertToFile(
                (condition.health_condition_media || []).filter((file) =>
                  isDocumentPickerAsset(file),
                ),
              );

              newFiles.forEach((file) => {
                formData.append(
                  `health_conditions[${index}][health_condition_media]`,
                  file,
                );
              });
            });

            await triggerUpdateResidentHealthConditions({
              resident: residentId,
              body: formData,
            }).unwrap();

            showNewToast({
              title: "Health Conditions and Medical History Updated",
              message:
                "Successfully updated resident's health conditions and medical history.",
              type: "success",
            });
            resolve(true);
          } catch (e) {
            const result = handleBackendError(e, setError, setErrorMessage);
            showErrorToast(e);
            resolve(result);
          }
        },
        (errors) => {
          console.error("Form errors:", errors);
          resolve(false);
        },
      )();
    });
  };

  return {
    control,
    errorMessage,
    submitForm,
  };
};
